import { DATE_FORMAT } from '@/libs/core/constants'
import FormCreator from '@/shared/FormCreator'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import Table from '@/shared/Table'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RefreshIcon from '@mui/icons-material/Refresh'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ArticleIcon from '@mui/icons-material/Article'
import CheckIcon from '@mui/icons-material/Check'
import DownloadIcon from '@mui/icons-material/Download'
import EmailIcon from '@mui/icons-material/Email'
import GroupIcon from '@mui/icons-material/Group'
import PreviewIcon from '@mui/icons-material/Visibility'
import ScheduleIcon from '@mui/icons-material/Schedule'
import TableChartIcon from '@mui/icons-material/TableChart'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Container from '@mui/material/Container'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import debounce from '@mui/material/utils/debounce'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

// ── 多信箱 Chip 輸入元件 ──────────────────────────────────────────────────────

const EmailChipInput = ({ emails, onChange }) => {
  const [inputVal, setInputVal] = useState('')
  const [focused, setFocused] = useState(false)

  const addEmails = raw => {
    const parts = raw
      .split(/[;；\s]+/)
      .map(s => s.trim())
      .filter(Boolean)
    if (!parts.length) return
    onChange([...new Set([...emails, ...parts])])
    setInputVal('')
  }

  const handleKeyDown = e => {
    if (['Enter', ';'].includes(e.key)) {
      e.preventDefault()
      addEmails(inputVal)
    }
    if (e.key === 'Backspace' && !inputVal && emails.length) {
      onChange(emails.slice(0, -1))
    }
  }

  const handleBlur = () => {
    setFocused(false)
    if (inputVal.trim()) addEmails(inputVal)
  }

  return (
    <Box>
      <Typography fontSize={14} fontWeight={600} color="#2D2D6C" mb={1}>
        欲寄送信箱
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          alignItems: 'center',
          border: focused ? '2px solid #FF6700' : '1px solid #bdbdbd',
          borderRadius: 2,
          px: 1.5,
          py: 1,
          minHeight: 46,
          cursor: 'text',
          transition: 'border-color 0.15s',
        }}
        onClick={e => e.currentTarget.querySelector('input')?.focus()}
      >
        {emails.map(em => (
          <Chip
            key={em}
            label={em}
            size="small"
            onDelete={() => onChange(emails.filter(x => x !== em))}
            sx={{
              bgcolor: '#fff3e0',
              color: '#e65100',
              border: '1px solid #ffcc80',
              '& .MuiChip-deleteIcon': { color: '#e65100', '&:hover': { color: '#bf360c' } },
            }}
          />
        ))}
        <input
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          placeholder={emails.length ? '' : 'team@company.com'}
          style={{
            border: 'none',
            outline: 'none',
            flex: 1,
            minWidth: 180,
            fontSize: 14,
            padding: '2px 4px',
            background: 'transparent',
            color: '#333',
          }}
        />
      </Box>
      <Typography fontSize={12} color="text.secondary" mt={0.5}>
        以分號分隔新增多個信箱，或按 Enter 確認
      </Typography>
    </Box>
  )
}

// ── 批次匯出 Dialog ───────────────────────────────────────────────────────────

const BatchExportDialog = ({
  open,
  onClose,
  dispatch,
  actionType,
  idField,
  selectedIds = [],
  selectedCount,
  isExporting,
  title,
  unit = '群組',
  extraPayload = {},
  mode = 'export',
}) => {
  const actionLabel = mode === 'download' ? '下載' : '匯出'
  const { t } = useTranslation()
  const [formats, setFormats] = useState(['PDF'])
  const [exportError, setExportError] = useState('')

  const toggleFormat = fmt => {
    setFormats(prev => (prev.includes(fmt) ? prev.filter(f => f !== fmt) : [...prev, fmt]))
  }

  const handleClose = () => {
    setFormats(['PDF'])
    setExportError('')
    onClose()
  }

  const handleExport = () => {
    setExportError('')
    dispatch({
      type: actionType,
      payload: { [idField]: selectedIds, formats, ...extraPayload },
      successCallback: () => {
        handleClose()
      },
      errorCallback: () => {
        const errorMessage =
          mode === 'download' ? t('errors.download_failed') : t('errors.export_failed')
        setExportError(errorMessage)
        dispatch({
          type: 'app/notify',
          payload: { message: errorMessage, severity: 'error' },
        })
      },
    })
  }

  const FormatCard = ({ fmt, icon }) => {
    const isSelected = formats.includes(fmt)
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={1}
        onClick={() => toggleFormat(fmt)}
        sx={{
          width: 140,
          height: 120,
          border: '2px solid',
          borderColor: isSelected ? '#f97316' : '#e0e0e0',
          borderRadius: 3,
          cursor: 'pointer',
          position: 'relative',
          bgcolor: isSelected ? '#fff7ed' : '#fafafa',
          transition: 'border-color 0.15s, background-color 0.15s',
          userSelect: 'none',
          '&:hover': { borderColor: '#f97316' },
        }}
      >
        {/* 右上角勾選框 */}
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 20,
            height: 20,
            borderRadius: 1,
            border: '2px solid',
            borderColor: isSelected ? '#f97316' : '#bdbdbd',
            bgcolor: isSelected ? '#f97316' : 'transparent',
          }}
        >
          {isSelected && <CheckIcon sx={{ fontSize: 13, color: 'white' }} />}
        </Stack>
        {icon}
        <Typography
          variant="body2"
          fontWeight={600}
          color={isSelected ? '#f97316' : 'text.secondary'}
        >
          {fmt}
        </Typography>
      </Stack>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, minHeight: 380 } }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0.5 }}
      >
        <Typography component="span" fontWeight={600} fontSize={16}>
          {title || `批次${actionLabel}報告`}
        </Typography>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'text.secondary' }}>
          ✕
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {/* 已選擇提示 */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ border: '1px solid #e0e0e0', borderRadius: 2, px: 2, py: 1.5, mb: 5 }}
        >
          <GroupIcon sx={{ fontSize: 18, color: '#f97316' }} />
          <Typography variant="body2">
            已選擇{' '}
            <span style={{ color: '#f97316', fontWeight: 700 }}>
              {selectedCount} 位{unit}
            </span>
          </Typography>
        </Stack>

        <Typography fontSize={14} fontWeight={600} color="#2D2D6C" mb={2.5}>
          選取{actionLabel}格式
        </Typography>

        <Stack direction="row" spacing={2} justifyContent="center">
          <FormatCard
            fmt="PDF"
            icon={
              <ArticleIcon
                sx={{ fontSize: 36, color: formats.includes('PDF') ? '#f97316' : '#bdbdbd' }}
              />
            }
          />
          <FormatCard
            fmt="Excel"
            icon={
              <TableChartIcon
                sx={{ fontSize: 36, color: formats.includes('Excel') ? '#f97316' : '#bdbdbd' }}
              />
            }
          />
        </Stack>

        {exportError && (
          <Typography variant="body2" color="error" mt={2.5}>
            {exportError}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5 }}>
        <Button variant="text" onClick={handleClose} sx={{ color: 'text.secondary' }}>
          取消
        </Button>
        <Button
          variant="contained"
          disabled={formats.length === 0 || isExporting}
          onClick={handleExport}
          sx={{
            bgcolor: '#f97316',
            borderRadius: 2,
            px: 3,
            '&:hover': { bgcolor: '#ea6a0a' },
            '&.Mui-disabled': { bgcolor: '#fcd9b6', color: 'white' },
          }}
        >
          {isExporting ? `${actionLabel}中...` : actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── 批次寄送 Dialog ───────────────────────────────────────────────────────────

// 寄送時間規則須與後端 TranscriptReportSendQueueServiceImpl 一致：請求到達當下起算至少 15 分鐘後（毫秒級判斷）。
// 預設時間為 15 分鐘後並無條件進位到整分（保證不少於 15 分鐘）。使用者未修改日期/時間（timeTouched 為 false）時，
// 送出當下改以「當下時間 + 15 分鐘 + SCHEDULE_SUBMIT_MARGIN_MS」寄送，對話框停留多久預設值都不會過期；
// 使用者自行修改過時間則送出前以當下時間再驗證一次，並保留 SCHEDULE_SUBMIT_MARGIN_MS 吸收網路延遲
const MIN_SCHEDULE_LEAD_MS = 15 * 60 * 1000
const SCHEDULE_SUBMIT_MARGIN_MS = 30 * 1000
const SCHEDULE_TIME_ERROR = '寄送時間須為現在時間至少 15 分鐘後'
const getDefaultScheduleTime = () => {
  const t = moment().add(MIN_SCHEDULE_LEAD_MS, 'ms')
  return t.seconds() || t.milliseconds() ? t.add(1, 'minute').startOf('minute') : t
}
const combineScheduleDateTime = (date, time) =>
  date?.isValid() && time?.isValid()
    ? date.clone().hour(time.hour()).minute(time.minute()).second(0).millisecond(0)
    : null
const isScheduleTimeValid = (dateTime, marginMs = 0) =>
  !!dateTime && dateTime.diff(moment()) >= MIN_SCHEDULE_LEAD_MS + marginMs
// 回傳實際送出的排程時間；使用者修改過且已不足 15 分鐘時回傳 null（由呼叫端顯示錯誤、不送出）
const resolveSubmitScheduleTime = (dateTime, timeTouched) => {
  if (!timeTouched) return moment().add(MIN_SCHEDULE_LEAD_MS + SCHEDULE_SUBMIT_MARGIN_MS, 'ms')
  return isScheduleTimeValid(dateTime, SCHEDULE_SUBMIT_MARGIN_MS) ? dateTime : null
}

const BatchSendDialog = ({
  open,
  onClose,
  dispatch,
  actionType,
  idField,
  selectedIds = [],
  selectedCount,
  isSending,
  title,
  unit = '群組',
  extraPayload = {},
  // 個人寄送的收件人固定為受測者本人 email（後端 enqueueUserSend 決定），不需輸入收件信箱
  needRecipientEmails = true,
}) => {
  const { t } = useTranslation()
  const [formats, setFormats] = useState(['PDF'])
  const [sendDate, setSendDate] = useState(getDefaultScheduleTime)
  const [sendTime, setSendTime] = useState(getDefaultScheduleTime)
  const [timeTouched, setTimeTouched] = useState(false)
  const [emails, setEmails] = useState([])
  const [sendError, setSendError] = useState('')

  useEffect(() => {
    if (open) {
      const init = getDefaultScheduleTime()
      setSendDate(init.clone())
      setSendTime(init.clone())
      setTimeTouched(false)
      setEmails([])
    }
  }, [open])

  const toggleFormat = fmt => {
    setFormats(prev => (prev.includes(fmt) ? prev.filter(f => f !== fmt) : [...prev, fmt]))
  }

  const handleClose = () => {
    setFormats(['PDF'])
    setSendDate(null)
    setSendTime(null)
    setTimeTouched(false)
    setEmails([])
    setSendError('')
    onClose()
  }

  const sendDateTime = combineScheduleDateTime(sendDate, sendTime)
  const isTimeValid = isScheduleTimeValid(sendDateTime)
  const timeError = timeTouched && sendDateTime && !isTimeValid ? SCHEDULE_TIME_ERROR : ''

  // 未修改時間時一律可送出（送出當下重新計算 15 分鐘後，見 resolveSubmitScheduleTime）
  const canSend =
    formats.length > 0 &&
    (!needRecipientEmails || emails.length > 0) &&
    (!timeTouched || isTimeValid)

  const handleSend = () => {
    setSendError('')
    // 對話框停留期間時間會流逝，送出當下再決定一次寄送時間，不送出注定被後端拒絕的請求
    const scheduledTime = resolveSubmitScheduleTime(sendDateTime, timeTouched)
    if (!scheduledTime) {
      setSendError(SCHEDULE_TIME_ERROR)
      return
    }
    dispatch({
      type: actionType,
      payload: {
        [idField]: selectedIds,
        formats,
        ...(needRecipientEmails ? { recipientEmails: emails } : {}),
        scheduledTime: scheduledTime.toISOString(),
        ...extraPayload,
      },
      successCallback: () => {
        dispatch({
          type: 'app/notify',
          payload: { message: '已排入寄送佇列', severity: 'success' },
        })
        handleClose()
      },
      errorCallback: message => {
        const errorMessage = message || t('errors.send_failed', '寄送失敗，請稍後再試')
        setSendError(errorMessage)
        dispatch({
          type: 'app/notify',
          payload: { message: errorMessage, severity: 'error' },
        })
      },
    })
  }

  const FormatCard = ({ fmt, icon }) => {
    const isSelected = formats.includes(fmt)
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={1}
        onClick={() => toggleFormat(fmt)}
        sx={{
          width: 140,
          height: 120,
          border: '2px solid',
          borderColor: isSelected ? '#f97316' : '#e0e0e0',
          borderRadius: 3,
          cursor: 'pointer',
          position: 'relative',
          bgcolor: isSelected ? '#fff7ed' : '#fafafa',
          transition: 'border-color 0.15s, background-color 0.15s',
          userSelect: 'none',
          '&:hover': { borderColor: '#f97316' },
        }}
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 20,
            height: 20,
            borderRadius: 1,
            border: '2px solid',
            borderColor: isSelected ? '#f97316' : '#bdbdbd',
            bgcolor: isSelected ? '#f97316' : 'transparent',
          }}
        >
          {isSelected && <CheckIcon sx={{ fontSize: 13, color: 'white' }} />}
        </Stack>
        {icon}
        <Typography
          variant="body2"
          fontWeight={600}
          color={isSelected ? '#f97316' : 'text.secondary'}
        >
          {fmt}
        </Typography>
      </Stack>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0.5 }}
      >
        <Typography component="span" fontWeight={600} fontSize={16}>
          {title || '批次寄送報告'}
        </Typography>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'text.secondary' }}>
          ✕
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {/* 已選擇提示 */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ border: '1px solid #e0e0e0', borderRadius: 2, px: 2, py: 1.5, mb: 5 }}
        >
          <GroupIcon sx={{ fontSize: 18, color: '#f97316' }} />
          <Typography variant="body2">
            已選擇{' '}
            <span style={{ color: '#f97316', fontWeight: 700 }}>
              {selectedCount} 位{unit}
            </span>
          </Typography>
        </Stack>

        {/* 格式選擇 */}
        <Typography fontSize={14} fontWeight={600} color="#2D2D6C" mb={2}>
          選取匯出格式
        </Typography>
        <Stack direction="row" spacing={2} mb={5} justifyContent="center">
          <FormatCard
            fmt="PDF"
            icon={
              <ArticleIcon
                sx={{ fontSize: 36, color: formats.includes('PDF') ? '#f97316' : '#bdbdbd' }}
              />
            }
          />
          <FormatCard
            fmt="Excel"
            icon={
              <TableChartIcon
                sx={{ fontSize: 36, color: formats.includes('Excel') ? '#f97316' : '#bdbdbd' }}
              />
            }
          />
        </Stack>

        {/* 欲寄送信箱（個人寄送不顯示，收件人為受測者本人） */}
        {needRecipientEmails && (
          <Box mb={5}>
            <EmailChipInput emails={emails} onChange={setEmails} />
          </Box>
        )}

        {/* 選取寄送時間 */}
        <Typography fontSize={14} fontWeight={600} color="#2D2D6C" mb={2}>
          選取寄送時間
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              value={sendDate}
              onChange={val => {
                setSendDate(val)
                setTimeTouched(true)
              }}
              inputFormat="YYYY-MM-DD"
              disablePast
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  sx={{ width: 180, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <TimePicker
              value={sendTime}
              onChange={val => {
                setSendTime(val)
                setTimeTouched(true)
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  error={!!timeError}
                  helperText={timeError}
                  sx={{ width: 160, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
            />
          </LocalizationProvider>
        </Stack>

        {sendError && (
          <Typography variant="body2" color="error" mt={2.5}>
            {sendError}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5 }}>
        <Button variant="text" onClick={handleClose} sx={{ color: 'text.secondary' }}>
          取消
        </Button>
        <Button
          variant="contained"
          disabled={!canSend || isSending}
          onClick={handleSend}
          sx={{
            bgcolor: '#f97316',
            borderRadius: 2,
            px: 3,
            '&:hover': { bgcolor: '#ea6a0a' },
            '&.Mui-disabled': { bgcolor: '#fcd9b6', color: 'white' },
          }}
        >
          {isSending ? '寄送中...' : '寄送'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Redux 連接 ────────────────────────────────────────────────────────────────

// module-level cache：路由切換時 DeptListView unmount，用此保留篩選狀態
let deptListStateCache = null
let navigatingToUserPage = false

const mapStateToProps = ({ transcript, loading }) => ({
  deptList: transcript.deptList,
  userList: transcript.userList,
  isDeptLoading: loading['transcript/getDepts'],
  isUserLoading: loading['transcript/getUsers'],
  isExportingDept: loading['transcript/exportDeptReports'],
  isExportingUser: loading['transcript/exportUserReports'],
  isSendingDept: loading['transcript/sendDeptReports'],
  isSendingUser: loading['transcript/sendUserReports'],
})

// ── 常數 ──────────────────────────────────────────────────────────────────────

// 快速篩選「近 N 個月」：今天往前 N 個月 ~ 今天（成績單看的是已執行的活動，不往後延伸）
const getRecentMonthsRange = months => ({
  startDate: moment().subtract(months, 'month').toDate(),
  endDate: moment().toDate(),
})

// 成績單報告寄送狀態篩選選項，與 ReportSendStatusChip 的文字對應一致
const EMAIL_STATUS_OPTIONS = [
  { value: 'ALL', label: '全部' },
  { value: 'TBS', label: '等待' },
  { value: 'PRO', label: '寄送中' },
  { value: 'SUC', label: '成功' },
  { value: 'FAIL', label: '失敗' },
  { value: 'UV', label: '域名驗證失敗' },
]
// 部門寄送已不驗證收件人網域（email-sending 2026-10-14 移除），部門列不再有「域名驗證失敗」的概念：
// 篩選選項不列出 UV，既有歷史 UV 記錄（實際未寄出）一律以「失敗」顯示與篩選。
// 個人寄送仍會驗證網域，個人列維持完整選項與原狀態顯示
const DEPT_EMAIL_STATUS_OPTIONS = EMAIL_STATUS_OPTIONS.filter(opt => opt.value !== 'UV')
const toDeptStatus = status => (status === 'UV' ? 'FAIL' : status)

// 成績單報告寄送狀態 chip（獨立於本頁其他統計的演練測試信寄送狀態）
// 對應 reportSendStatus：TBS(待寄送)／PRO(處理中)／SUC(寄送成功)／FAIL(寄送失敗)／UV(收件人域名驗證失敗，已跳過寄送)
// 從未寄送過成績單時 reportSendStatus 為 null/undefined，顯示空值，不誤判為任何中間狀態
const REPORT_SEND_STATUS_CONFIG = {
  TBS: { label: '等待', color: 'default' },
  PRO: { label: '寄送中', color: 'info' },
  SUC: { label: '成功', color: 'success' },
  FAIL: { label: '失敗', color: 'error' },
  UV: { label: '域名驗證失敗', color: 'warning' },
}
const getReportSendStatusConfig = value =>
  REPORT_SEND_STATUS_CONFIG[value] || { label: value, color: 'default' }

// 部門列的 reportSendStatus 為最新一次寄送所有收件人的彙總狀態（任一失敗即顯示失敗），
// reportRecipients 為各收件人明細：部門列不論彙總狀態為何，chip 文字一律加上「成功數/總數」，
// 滑過 chip 以 tooltip 列出每位收件人的結果，讓使用者知道寄給了誰、要補寄給誰
const RecipientStatusTooltip = ({ recipients }) => (
  <Box sx={{ py: 0.5 }}>
    {recipients.map(r => (
      <Box key={r.email} sx={{ fontSize: 12, lineHeight: 1.6 }}>
        {r.email}：{getReportSendStatusConfig(toDeptStatus(r.status)).label}
      </Box>
    ))}
  </Box>
)

// isDept：部門列將 UV 視為 FAIL 顯示（見 DEPT_EMAIL_STATUS_OPTIONS 說明）
const ReportSendStatusChip = ({ row, isDept = false }) => {
  const value = isDept ? toDeptStatus(row?.reportSendStatus) : row?.reportSendStatus
  if (!value) return <></>
  const config = getReportSendStatusConfig(value)
  const recipients = row?.reportRecipients || []
  if (recipients.length === 0) {
    return <Chip label={config.label} size="small" color={config.color} variant="outlined" />
  }
  const sucCount = recipients.filter(r => r.status === 'SUC').length
  const label = isDept ? `${config.label} ${sucCount}/${recipients.length}` : config.label
  return (
    <Tooltip title={<RecipientStatusTooltip recipients={recipients} />} arrow>
      <Chip label={label} size="small" color={config.color} variant="outlined" />
    </Tooltip>
  )
}

// 活動標頭列 key = "h__campId"，群組子列 key = campaignId（唯一）
const getDeptRowId = row => (row.isHeader ? `h__${row.campId}` : `c__${row.campaignId}`)

// 部門列表「信件寄送狀態」排序依寄送流程：等待 → 寄送中 → 成功 → 失敗（而非英文狀態碼字母順序）；
// UV 於部門列顯示為失敗，排序同失敗
const DEPT_REPORT_SEND_STATUS_SORT_RANK = { TBS: 1, PRO: 2, SUC: 3, FAIL: 4 }

// 部門列「信件寄送狀態」排序值：狀態依流程排名，狀態相同時再依成功比例（成功數/收件人數）；從未寄送回傳 null（排最後）
const deptReportSendStatusSortValue = row => {
  const status = toDeptStatus(row?.reportSendStatus)
  if (!status) return null
  const rank = DEPT_REPORT_SEND_STATUS_SORT_RANK[status] ?? 99
  const recipients = row?.reportRecipients || []
  if (recipients.length === 0) return rank
  const sucRatio = recipients.filter(r => r.status === 'SUC').length / recipients.length
  return rank + sucRatio * 0.9
}

// 部門列表排序比較：空值不論升降冪一律排最後；數字依大小，其餘（含日期字串）依中文語系、數字感知比較
const compareSortValues = (a, b, factor) => {
  const emptyA = a == null || a === ''
  const emptyB = b == null || b === ''
  if (emptyA && emptyB) return 0
  if (emptyA) return 1
  if (emptyB) return -1
  if (typeof a === 'number' && typeof b === 'number') return factor * (a - b)
  return factor * String(a).localeCompare(String(b), 'zh-Hant', { numeric: true })
}

// 依欄位排序活動群組：先排序各活動內的子列，再依活動名稱（所屬活動欄）或排序後第一筆子列的值
// 決定活動先後，讓子列始終跟在所屬活動標頭列下方，不會被平面排序拆散
const sortCampGroups = (groups, sortItem) => {
  if (!sortItem?.field || !sortItem.sort) return groups
  const { field, sort } = sortItem
  const factor = sort === 'desc' ? -1 : 1
  const valueOf = row => {
    if (field === 'campName') return row.campaignName || row.campName
    // 活動日期轉為時間戳比較，不受後端日期序列化格式（字串／epoch）影響
    if (field === 'startTime') return row.startTime ? moment(row.startTime).valueOf() : null
    if (field === 'reportSendStatus') return deptReportSendStatusSortValue(row)
    return row[field]
  }
  const sortedGroups = groups.map(group => ({
    ...group,
    rows: [...group.rows].sort((x, y) => compareSortValues(valueOf(x), valueOf(y), factor)),
  }))
  return sortedGroups.sort((x, y) =>
    field === 'campName'
      ? compareSortValues(x.campName, y.campName, factor)
      : compareSortValues(valueOf(x.rows[0] || {}), valueOf(y.rows[0] || {}), factor),
  )
}

// ── 信件預覽 Dialog（部門／個人共用）────────────────────────────────────────
// 內容由後端 email-preview API 以實際寄送的同一套模板組裝（單位名稱取受測公司、部門取部門名稱、
// 個人姓名已去識別化），前端不自行拼湊欄位，確保預覽與實際寄出的信件內容一致

const MailPreviewDialog = ({ open, onClose, dispatch, actionType, payload }) => {
  const [content, setContent] = useState(null)
  const [error, setError] = useState(false)
  const payloadKey = JSON.stringify(payload)

  useEffect(() => {
    if (!open || !payload) return undefined
    let active = true
    setContent(null)
    setError(false)
    dispatch({
      type: actionType,
      payload,
      successCallback: data => active && setContent(data?.content ?? ''),
      errorCallback: () => active && setError(true),
    })
    // 關閉或切換對象後，忽略前一次尚未回來的回應
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, actionType, payloadKey])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0.5 }}
      >
        <Typography component="span" fontWeight={600} fontSize={16}>
          信件預覽
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <Typography fontSize={18}>✕</Typography>
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            p: 3,
            fontSize: 14,
            color: '#2D2D6C',
            '& p': { m: 0, mb: 0.5 },
            '& hr': { border: 0, borderTop: '1px solid #e0e0e0', my: 2 },
          }}
        >
          {error && (
            <Typography fontSize={14} color="error">
              信件預覽載入失敗，請稍後再試
            </Typography>
          )}
          {!error && content == null && (
            <Typography fontSize={14} color="text.secondary">
              載入中…
            </Typography>
          )}
          {/* 後端組信時已對代入的資料做 HTML 跳脫 */}
          {!error && content != null && <Box dangerouslySetInnerHTML={{ __html: content }} />}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>關閉</Button>
      </DialogActions>
    </Dialog>
  )
}

// ── 部門列表（預設畫面）──────────────────────────────────────────────────────

const DeptListView = ({
  dispatch,
  deptList,
  isDeptLoading,
  isExportingDept,
  isSendingDept,
  stateCache,
  onSaveState,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const form = useRef()
  // 第二列（自訂期間／快速篩選）是另一個 FormCreator，需各自持有 ref 才能以 setValue 同步顯示
  const dateForm = useRef()

  const { data = [], paging = {} } = deptList || {}

  const sc = stateCache || {}

  const [filterDateRange, setFilterDateRange] = useState(
    sc.filterDateRange || getRecentMonthsRange(sc.selectedDateOpt ?? 6),
  )
  const [searchText, setSearchText] = useState(sc.searchText || '')
  const debouncedSetSearchText = useRef(debounce(v => setSearchText(v ?? ''), 400)).current
  const [emailSendStatus, setEmailSendStatus] = useState(sc.emailSendStatus || 'ALL')
  const [selectedDateOpt, setSelectedDateOpt] = useState(sc.selectedDateOpt ?? 6)
  const [isCustomRange, setIsCustomRange] = useState(sc.isCustomRange || false)
  const [page, setPage] = useState(sc.page || 0)
  const [pageSize, setPageSize] = useState(sc.pageSize || 10)
  const [selected, setSelected] = useState([])
  const [downloadRow, setDownloadRow] = useState(null)
  const [expandedCampIds, setExpandedCampIds] = useState(new Set(sc.expandedCampIds || []))
  // 欄位排序狀態：由 sortCampGroups 自行排序（DataGrid 設為 server sortingMode，不對平面列重排）
  const [deptSortModel, setDeptSortModel] = useState([])
  const [selectedCampId, setSelectedCampId] = useState(sc.selectedCampId || null)
  const [selectedDropOption, setSelectedDropOption] = useState(sc.selectedDropOption || null)
  const [campFilter, setCampFilter] = useState(sc.campFilter || '')
  const [deptPreviewRow, setDeptPreviewRow] = useState(null)
  const [deptSendRow, setDeptSendRow] = useState(null)
  const [deptScheduleRow, setDeptScheduleRow] = useState(null)

  // 活動下拉獨立資料（分頁，不受快速搜尋影響）
  const [campDropList, setCampDropList] = useState([])
  const [campDropPage, setCampDropPage] = useState(0)
  const [campDropTotal, setCampDropTotal] = useState(0)
  const [campDropLoading, setCampDropLoading] = useState(false)
  const campDropPageSize = 10
  const [campDropKeyword, setCampDropKeyword] = useState('')
  const [campDropOpen, setCampDropOpen] = useState(false)
  const campDropExpandingRef = useRef(false)
  const [expandedDropCampIds, setExpandedDropCampIds] = useState(new Set())
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [sendDialogOpen, setSendDialogOpen] = useState(false)

  const pagination = {
    ...paging,
    page,
    pageSize,
    onPageChange: setPage,
    onPageSizeChange: setPageSize,
  }

  const fetchData = useCallback(() => {
    // 成績單報告寄送狀態僅為前端當頁過濾（後端 emailSendStatus 參數篩選的是演練測試信狀態，
    // 語意不同，不應傳入 reportSendStatus 的篩選值），故固定不帶 emailSendStatus 給後端
    // 快速搜尋 keyword 交由後端於目前條件範圍內模糊比對（分頁前套用），搜尋涵蓋所有分頁
    const keyword = (searchText ?? '').trim()

    // campId 精確查詢：不需日期，後端直接走快速路徑
    if (selectedCampId != null) {
      dispatch({
        type: 'transcript/getDepts',
        payload: {
          campId: selectedCampId,
          campName: '',
          dept: '',
          keyword,
          emailSendStatus: '',
          pageIndex: page,
          pageSize,
        },
      })
      return
    }
    if (!filterDateRange && !campFilter) return
    const hasCampFilter = (campFilter ?? '').trim() !== ''
    dispatch({
      type: 'transcript/getDepts',
      payload: {
        campName: hasCampFilter ? campFilter : '',
        dept: '',
        keyword,
        startTime: hasCampFilter
          ? moment('2000-01-01').toISOString()
          : moment(filterDateRange.startDate).toISOString(),
        endTime: hasCampFilter
          ? moment().add(1, 'year').toISOString()
          : moment(filterDateRange.endDate).toISOString(),
        emailSendStatus: '',
        pageIndex: page,
        pageSize,
      },
    })
  }, [dispatch, selectedCampId, filterDateRange, campFilter, searchText, page, pageSize])

  const skipFirstFetchRef = useRef(!!stateCache)
  useEffect(() => {
    if (skipFirstFetchRef.current) {
      skipFirstFetchRef.current = false
      return
    }
    fetchData()
  }, [fetchData])

  const isRestoredRef = useRef(!!stateCache)

  // 還原 FormCreator 內部 react-hook-form 的值
  useEffect(() => {
    if (!stateCache) return
    if (stateCache.selectedDropOption) {
      form.current?.setValue?.('_campDrop', stateCache.selectedDropOption)
    }
    if (stateCache.searchText) {
      form.current?.setValue?.('search', stateCache.searchText)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isRestoredRef.current) {
      isRestoredRef.current = false
      return
    }
    setPage(0)
    setSelected([])
  }, [filterDateRange, campFilter, searchText, emailSendStatus])

  useEffect(() => {
    if (stateCache) return
    setEmailSendStatus('ALL')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campFilter])

  // 活動下拉分頁 fetch
  const fetchCampDrop = useCallback(
    (keyword, pageIndex, append) => {
      setCampDropLoading(true)
      dispatch({
        type: 'transcript/getCampList',
        payload: { keyword, pageIndex, pageSize: campDropPageSize },
        successCallback: resData => {
          setCampDropLoading(false)
          const items = resData?.data || []
          const total = resData?.paging?.total ?? 0
          setCampDropTotal(total)
          // 新 API 直接回傳 { campId, campName, groups[] } 結構
          if (append) {
            setCampDropList(prev => {
              const seen = new Set(prev.map(c => c.campId))
              return [...prev, ...items.filter(c => !seen.has(c.campId))]
            })
          } else {
            setCampDropList(items)
          }
        },
        errorCallback: () => setCampDropLoading(false),
      })
    },
    [dispatch],
  )

  // keyword 變更時從第 0 頁重新抓
  useEffect(() => {
    setCampDropPage(0)
    fetchCampDrop(campDropKeyword, 0, false)
  }, [campDropKeyword]) // eslint-disable-line react-hooks/exhaustive-deps

  const campDropSearchDebounce = useRef(debounce(value => setCampDropKeyword(value), 400)).current

  const handleCampDropLoadMore = useCallback(() => {
    const nextPage = campDropPage + 1
    if (nextPage * campDropPageSize >= campDropTotal) return
    setCampDropPage(nextPage)
    fetchCampDrop(campDropKeyword, nextPage, true)
  }, [campDropPage, campDropTotal, campDropKeyword, fetchCampDrop])

  // 展開後的 flat options（直接用後端回傳的 groups）
  // 若當前選中項目還未在清單中（非同步載入），先插入確保顯示正確
  const campDropOptionsRaw = campDropList.flatMap(camp => [
    {
      id: `camp__${camp.campId}`,
      label: camp.campName,
      campId: camp.campId,
      campName: camp.campName,
      isGroup: false,
      hasGroups: (camp.groups || []).length > 0,
    },
    ...(expandedDropCampIds.has(camp.campId)
      ? (camp.groups || []).map(g => ({
          id: `group__${g.campaignId}`,
          label: g.groupName,
          campId: camp.campId,
          campName: camp.campName,
          isGroup: true,
        }))
      : []),
  ])

  const campDropOptions = (() => {
    if (!selectedDropOption) return campDropOptionsRaw
    const exists = campDropOptionsRaw.some(o => o.id === selectedDropOption.id)
    return exists ? campDropOptionsRaw : [selectedDropOption, ...campDropOptionsRaw]
  })()

  const hasMoreCamps = (campDropPage + 1) * campDropPageSize < campDropTotal

  const isFirstDateOptRef = useRef(true)
  useEffect(() => {
    if (isFirstDateOptRef.current) {
      isFirstDateOptRef.current = false
      if (stateCache) return
    }
    if (selectedDateOpt !== '0' && selectedDateOpt !== 0) {
      const range = getRecentMonthsRange(selectedDateOpt)
      dateForm.current?.setValue?.('dateRangeFilter', range)
      setFilterDateRange(range)
      setIsCustomRange(false)
    } else {
      setIsCustomRange(true)
    }
  }, [selectedDateOpt])

  const toggleCamp = campId => {
    setExpandedCampIds(prev => {
      const next = new Set(prev)
      if (next.has(campId)) next.delete(campId)
      else next.add(campId)
      return next
    })
  }

  // 從當頁 data 去重建立活動下拉選單，維持後端 startTime 降冪順序
  const campMenuList = []
  const campMenuSeenIds = new Set()
  data.forEach(row => {
    if (!campMenuSeenIds.has(row.campId)) {
      campMenuSeenIds.add(row.campId)
      campMenuList.push({ campId: row.campId, campName: row.campName, groups: [] })
    }
    if (row.campaignId) {
      const camp = campMenuList.find(c => c.campId === row.campId)
      if (camp) camp.groups.push({ campaignId: row.campaignId, groupName: row.groupName })
    }
  })

  // 前端過濾：成績單報告寄送狀態（僅當頁過濾，後端尚未支援依 reportSendStatus 分頁查詢）；
  // 快速搜尋已改由後端處理（見 fetchData 的 keyword）
  const matchStatus = row => {
    if (!emailSendStatus || emailSendStatus === 'ALL') return true
    return toDeptStatus(row.reportSendStatus) === emailSendStatus
  }
  const filteredData = data.filter(matchStatus)

  // 依活動分組（維持後端順序），套用欄位排序後再展開：所有活動都顯示標頭列，展開後才顯示群組子列
  const campGroups = []
  const campGroupMap = new Map()
  filteredData.forEach(row => {
    if (!campGroupMap.has(row.campId)) {
      const group = { campId: row.campId, campName: row.campName, rows: [] }
      campGroupMap.set(row.campId, group)
      campGroups.push(group)
    }
    campGroupMap.get(row.campId).rows.push(row)
  })
  const visibleRows = sortCampGroups(campGroups, deptSortModel[0]).flatMap(group => [
    { campId: group.campId, campName: group.campName, isHeader: true },
    ...(expandedCampIds.has(group.campId)
      ? group.rows.map(row => ({ ...row, isHeader: false }))
      : []),
  ])

  const rowIds = visibleRows.map(r => getDeptRowId(r))

  const handleSelect = id => {
    setSelected(prev => (prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id]))
  }

  const allChildIds = filteredData.map(r => `c__${r.campaignId}`)
  const isAllSelected = allChildIds.length > 0 && allChildIds.every(id => selected.includes(id))

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelected([])
      setExpandedCampIds(new Set())
    } else {
      // 全選時確保全展開才能看到所有子列
      setExpandedCampIds(new Set(data.map(r => r.campId)))
      setSelected(allChildIds)
    }
  }

  // 活動狀態 chip
  const CampStatusChip = ({ value }) => {
    const colorMap = { IP: 'primary', COM: 'success', EC: 'success' }
    return (
      <Chip
        label={t(`status.${value}`, { defaultValue: value || '-' })}
        size="small"
        color={colorMap[value] || 'default'}
        variant="outlined"
      />
    )
  }

  const cols = [
    // 勾選
    {
      field: '__check',
      headerName: '',
      width: 70,
      sortable: false,
      renderHeader: () => (
        <Checkbox
          size="small"
          checked={isAllSelected}
          indeterminate={selected.length > 0 && !isAllSelected}
          onChange={handleSelectAll}
        />
      ),
      renderCell: ({ row }) => {
        if (row.isHeader) return null
        const id = getDeptRowId(row)
        return (
          <Checkbox
            size="small"
            checked={selected.includes(id)}
            onChange={() => handleSelect(id)}
            onClick={e => e.stopPropagation()}
          />
        )
      },
    },
    // 所屬活動（標頭列顯示主活動名稱＋展開箭頭；子列顯示子活動名稱，無子活動名稱時後端已退回主活動名稱，
    // 點擊子活動名稱進入該子活動的受測者（個人）列表）
    {
      field: 'campName',
      headerName: '所屬活動',
      // shared/Table 會以 width 作為欄位最小寬度（自身的 minWidth 會被覆蓋），故以 width 設定下限，flex 分配剩餘空間
      width: 280,
      flex: 2,
      renderCell: ({ row }) => {
        if (row.isHeader) {
          return (
            <Stack direction="row" alignItems="center" width="100%" spacing={0.5}>
              <IconButton
                component="div"
                size="small"
                sx={{ flexShrink: 0 }}
                onClick={e => {
                  e.stopPropagation()
                  toggleCamp(row.campId)
                }}
              >
                {expandedCampIds.has(row.campId) ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </IconButton>
              <Tooltip title={row.campName} placement="top-start">
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                  }}
                >
                  {row.campName}
                </Typography>
              </Tooltip>
            </Stack>
          )
        }
        const subName = row.campaignName || row.campName
        return (
          <Tooltip title={`${subName}（點擊查看受測者列表）`} placement="top-start">
            <Typography
              variant="body2"
              onClick={() => {
                navigatingToUserPage = true
                onSaveState?.({
                  filterDateRange,
                  searchText,
                  emailSendStatus,
                  selectedDateOpt,
                  isCustomRange,
                  page,
                  pageSize,
                  expandedCampIds: [...expandedCampIds],
                  selectedCampId,
                  selectedDropOption,
                  campFilter,
                })
                // 路由 state 供麵包屑 {{label}} 插值（DashboardLayout 以 state 做 i18n 插值，上一層已是「成績單檢視」）：
                // 子活動名稱與主活動相同（或無子活動名稱）時只顯示一次，不同時顯示「主活動 > 子活動」
                navigate(`/transcript/${row.campId}/${row.campaignId}`, {
                  state: {
                    label:
                      subName && subName !== row.campName
                        ? `${row.campName} > ${subName}`
                        : row.campName,
                  },
                })
              }}
              sx={{
                pl: 1.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: theme => theme.palette.accent_60?.main || '#3730a3',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {subName}
            </Typography>
          </Tooltip>
        )
      },
    },
    // 部門名稱（子列顯示該子活動受測者的部門 dept，標頭列空白）
    {
      field: 'dept',
      headerName: '部門名稱',
      minWidth: 140,
      flex: 1.2,
      renderCell: ({ row }) => {
        if (row.isHeader) return null
        return (
          <Tooltip title={row.dept || ''} placement="top-start">
            <Typography
              variant="body2"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {row.dept || '-'}
            </Typography>
          </Tooltip>
        )
      },
    },
    // 活動發送狀態
    {
      field: 'campStatus',
      headerName: '活動發送狀態',
      minWidth: 120,
      flex: 1,
      renderCell: ({ row }) => {
        if (row.isHeader) return null
        return <CampStatusChip value={row.campStatus} />
      },
    },
    // 活動日期
    {
      field: 'startTime',
      headerName: '活動日期',
      // 「YYYY-MM-DD ~ YYYY-MM-DD」含儲存格 padding 約需 220px；width 為最小寬度（同所屬活動欄說明），flex 分配剩餘空間
      width: 240,
      flex: 1.3,
      renderCell: ({ row }) => {
        if (row.isHeader) return null
        const s = row.startTime ? moment(row.startTime).format(DATE_FORMAT) : '?'
        const e = row.endTime ? moment(row.endTime).format(DATE_FORMAT) : '?'
        return (
          <Typography variant="body2" noWrap>
            {s} ~ {e}
          </Typography>
        )
      },
    },
    // 群組受測人數
    {
      field: 'userCount',
      headerName: '部門受測人數',
      minWidth: 90,
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => {
        if (row.isHeader) return null
        return <Typography variant="body2">{row.userCount ?? '-'}</Typography>
      },
    },
    // 信件預覽
    {
      field: '__preview',
      headerName: '信件預覽',
      width: 96,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => {
        if (row.isHeader) return null
        return (
          <Tooltip title="信件預覽">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation()
                setDeptPreviewRow(row)
              }}
            >
              <PreviewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      },
    },
    // 信件寄送
    {
      field: '__send',
      headerName: '信件寄送',
      width: 96,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => {
        if (row.isHeader) return null
        return (
          <Tooltip title="信件寄送">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation()
                setDeptSendRow(row)
              }}
            >
              <EmailIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      },
    },
    // 寄送排程編輯
    {
      field: '__schedule',
      headerName: '寄送排程編輯',
      width: 120,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => {
        if (row.isHeader) return null
        return (
          <Tooltip title="寄送排程編輯">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation()
                setDeptScheduleRow(row)
              }}
            >
              <ScheduleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      },
    },
    // 成績單報告寄送狀態
    {
      field: 'reportSendStatus',
      headerName: '信件寄送狀態',
      minWidth: 110,
      flex: 1,
      renderCell: ({ row }) => {
        if (row.isHeader) return null
        return <ReportSendStatusChip row={row} isDept />
      },
    },
    // 下載群組報告
    {
      field: '__download',
      headerName: '下載部門報告',
      width: 130,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => {
        if (row.isHeader) return null
        return (
          <Tooltip title="下載群組報告">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation()
                setDownloadRow(row)
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      },
    },
  ]

  return (
    <Container maxWidth={false}>
      <Grid container spacing={2} flexDirection="column">
        <Grid item>
          <Card sx={{ mt: 2 }}>
            <CardContent>
              {/* 第一列：活動下拉 → 搜尋框 */}
              <FormCreator
                ref={form}
                sx={{ mb: 2, '& .MuiGrid-container': { flexWrap: 'nowrap', alignItems: 'center' } }}
                formItems={[
                  {
                    label: '活動',
                    name: '_campDrop',
                    defaultValue: null,
                    type: 'autocomplete',
                    inputConfig: {
                      sx: { width: 220 },
                      options: campDropOptions,
                      values: selectedDropOption,
                      onChange: newValue => {
                        if (!newValue) {
                          setSelectedCampId(null)
                          setSelectedDropOption(null)
                          setCampFilter('')
                        } else {
                          // 點選部門（群組）時查詢的仍是其所屬活動（campId），查詢框統一顯示活動名稱
                          const campOption = newValue.isGroup
                            ? campDropOptionsRaw.find(
                                o => !o.isGroup && o.campId === newValue.campId,
                              ) || {
                                id: `camp__${newValue.campId}`,
                                label: newValue.campName,
                                campId: newValue.campId,
                                campName: newValue.campName,
                                isGroup: false,
                              }
                            : newValue
                          if (newValue.isGroup) form.current?.setValue?.('_campDrop', campOption)
                          setSelectedCampId(campOption.campId)
                          setSelectedDropOption(campOption)
                          setCampFilter(campOption.campName)
                          setExpandedDropCampIds(new Set())
                        }
                      },
                      onInputChange: (_, value, reason) => {
                        if (['input', 'clear'].includes(reason)) {
                          campDropSearchDebounce(value)
                        }
                      },
                      isOptionEqualToValue: (option, value) => option.id === value.id,
                      noOptionsText: campDropLoading ? '載入中…' : '無活動資料',
                      loading: campDropLoading,
                      filterOptions: opts => opts,
                      open: campDropOpen,
                      onOpen: () => setCampDropOpen(true),
                      onClose: (_, reason) => {
                        if (campDropExpandingRef.current) {
                          campDropExpandingRef.current = false
                          return
                        }
                        setCampDropOpen(false)
                      },
                      onLoadMore:
                        hasMoreCamps && !campDropLoading ? handleCampDropLoadMore : undefined,
                      renderOption: (props, option) => {
                        const isSelected = selectedDropOption?.id === option.id
                        if (!option.isGroup) {
                          const isExpanded = expandedDropCampIds.has(option.campId)
                          return (
                            <li
                              {...props}
                              key={option.id}
                              onClick={e => props.onClick?.(e)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingLeft: 16,
                                paddingRight: 8,
                                backgroundColor: isSelected ? '#e8eaf6' : undefined,
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                style={{ flex: 1, color: isSelected ? '#FF6700' : '#2D2D6C' }}
                              >
                                {option.label}
                              </Typography>
                              {option.hasGroups && (
                                <IconButton
                                  size="small"
                                  onMouseDown={e => {
                                    e.stopPropagation()
                                    campDropExpandingRef.current = true
                                  }}
                                  onClick={e => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setExpandedDropCampIds(prev => {
                                      const next = new Set(prev)
                                      if (next.has(option.campId)) next.delete(option.campId)
                                      else next.add(option.campId)
                                      return next
                                    })
                                  }}
                                >
                                  {isExpanded ? (
                                    <ArrowDropUpIcon fontSize="small" />
                                  ) : (
                                    <ArrowDropDownIcon fontSize="small" />
                                  )}
                                </IconButton>
                              )}
                            </li>
                          )
                        }
                        return (
                          <li
                            {...props}
                            key={option.id}
                            style={{
                              ...props.style,
                              paddingLeft: 32,
                              backgroundColor: isSelected ? '#e8eaf6' : undefined,
                              color: isSelected ? '#FF6700' : '#C050E8',
                            }}
                          >
                            {option.label}
                          </li>
                        )
                      },
                      popupIcon: <KeyboardArrowDownIcon />,
                    },
                  },
                  {
                    label: t('general.quick_search'),
                    name: 'search',
                    defaultValue: '',
                    inputConfig: {
                      onSubmit: value => setSearchText(value),
                      onKeyUp: e => debouncedSetSearchText(e.target.value),
                      sx: { width: 220 },
                    },
                  },
                ]}
                flexDirection="row"
                submitComponent={() => null}
                resetComponent={() => null}
              />

              {/* 第二列：自訂時間 → 快速篩選 */}
              <Stack direction="row" alignItems="center" flexWrap="nowrap" gap={1} mb={2}>
                <FormCreator
                  ref={dateForm}
                  sx={{ '& .MuiGrid-container': { flexWrap: 'nowrap', alignItems: 'center' } }}
                  formItems={[
                    {
                      label: t('general.custom_date_range'),
                      name: 'dateRangeFilter',
                      type: 'dateRange',
                      disabled: !isCustomRange,
                      // 初始即顯示實際查詢用的區間（預設近 6 個月；返回本頁時還原先前區間）
                      defaultValue: filterDateRange || getRecentMonthsRange(6),
                      inputConfig: { onChange: value => setFilterDateRange(value) },
                    },
                    {
                      label: t('general.quick_filter'),
                      name: 'setDateRange',
                      type: 'radio',
                      defaultValue: selectedDateOpt,
                      inputConfig: {
                        onChange: e => setSelectedDateOpt(e.target.value),
                        options: [
                          { value: 1, label: t('general.month_num', { num: 1 }) },
                          { value: 2, label: t('general.month_num', { num: 2 }) },
                          { value: 3, label: t('general.month_num', { num: 3 }) },
                          { value: 6, label: t('general.month_num', { num: 6 }) },
                          { value: 0, label: t('general.custom') },
                        ],
                      },
                    },
                  ]}
                  flexDirection="row"
                  submitComponent={() => null}
                  resetComponent={() => null}
                />
              </Stack>

              {/* 第三列：全選 → 信件寄送狀態 → 批次匯出 → 批次寄送 */}
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={1}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={selected.length > 0 && !isAllSelected}
                    onChange={handleSelectAll}
                  />
                  <Typography
                    fontWeight={600}
                    sx={{ fontSize: 14, whiteSpace: 'nowrap', color: '#2D2D6C' }}
                  >
                    全選（已選 {selected.length} 個群組）
                  </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    fontWeight={600}
                    sx={{ fontSize: 14, whiteSpace: 'nowrap', color: '#2D2D6C' }}
                  >
                    信件寄送狀態
                  </Typography>
                  <Select
                    size="small"
                    value={emailSendStatus}
                    onChange={e => setEmailSendStatus(e.target.value)}
                    sx={{
                      width: 180,
                      fontSize: 14,
                      borderRadius: '3px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2D2D6C' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2D2D6C' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#FF6700',
                        borderWidth: 2,
                      },
                    }}
                  >
                    {DEPT_EMAIL_STATUS_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={selected.length === 0}
                  onClick={() => setExportDialogOpen(true)}
                >
                  批次匯出部門報告（{selected.length}）
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  disabled={selected.length === 0}
                  onClick={() => setSendDialogOpen(true)}
                >
                  批次寄送部門報告（{selected.length}）
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchData()}
                  sx={{
                    color: '#2e7d32',
                    borderColor: '#2e7d32',
                    '&:hover': { borderColor: '#1b5e20', color: '#1b5e20', bgcolor: '#f1f8e9' },
                  }}
                >
                  重新整理
                </Button>

                <BatchExportDialog
                  open={exportDialogOpen}
                  onClose={() => setExportDialogOpen(false)}
                  dispatch={dispatch}
                  actionType="transcript/exportDeptReports"
                  idField="campaignIds"
                  selectedIds={selected.map(id => id.replace(/^c__/, ''))}
                  selectedCount={selected.length}
                  isExporting={isExportingDept}
                  title="批次匯出部門報告"
                />
                <BatchSendDialog
                  open={sendDialogOpen}
                  onClose={() => setSendDialogOpen(false)}
                  dispatch={dispatch}
                  actionType="transcript/sendDeptReports"
                  idField="campaignIds"
                  selectedIds={selected.map(id => id.replace(/^c__/, ''))}
                  selectedCount={selected.length}
                  isSending={isSendingDept}
                  title="批次寄送部門報告"
                />
                <BatchExportDialog
                  mode="download"
                  open={!!downloadRow}
                  onClose={() => setDownloadRow(null)}
                  dispatch={dispatch}
                  actionType="transcript/exportDeptReports"
                  idField="campaignIds"
                  selectedIds={downloadRow ? [downloadRow.campaignId] : []}
                  selectedCount={1}
                  isExporting={isExportingDept}
                  title="下載群組報告"
                />
              </Stack>
            </CardContent>

            <Table
              minHeight={400}
              columns={cols}
              rows={visibleRows}
              dataGridConfig={{
                getRowId: row => getDeptRowId(row),
                loading: isDeptLoading,
                disableRowSelectionOnClick: true,
                getRowHeight: ({ model }) => (model.isHeader ? 48 : 52),
                // 排序改由 sortCampGroups 依活動分組處理；欄位定義 sortable: false 的欄位忽略排序
                sortingMode: 'server',
                sortModel: deptSortModel,
                onSortModelChange: model =>
                  setDeptSortModel(
                    model.filter(m => cols.find(c => c.field === m.field)?.sortable !== false),
                  ),
              }}
              noRowsLabel={t('errors.no_campaign')}
              pagination={pagination}
            />
          </Card>
        </Grid>
      </Grid>
      <MailPreviewDialog
        open={!!deptPreviewRow}
        onClose={() => setDeptPreviewRow(null)}
        dispatch={dispatch}
        actionType="transcript/getDeptEmailPreview"
        payload={deptPreviewRow ? { campaignId: deptPreviewRow.campaignId } : null}
      />
      <SendConfirmDialog
        open={!!deptSendRow}
        onClose={() => setDeptSendRow(null)}
        dispatch={dispatch}
        row={deptSendRow}
        unit="dept"
        isSending={isSendingDept}
      />
      <ScheduleEditDialog
        open={!!deptScheduleRow}
        onClose={() => setDeptScheduleRow(null)}
        dispatch={dispatch}
        row={deptScheduleRow}
        unit="dept"
        isSending={isSendingDept}
      />
    </Container>
  )
}

// ── 寄送排程編輯 Dialog ───────────────────────────────────────────────────────

const ScheduleEditDialog = ({ open, onClose, dispatch, row, unit = 'user', isSending }) => {
  const [schedDate, setSchedDate] = useState(getDefaultScheduleTime)
  const [schedTime, setSchedTime] = useState(getDefaultScheduleTime)
  const [emails, setEmails] = useState([])
  const [timeTouched, setTimeTouched] = useState(false)
  const [scheduleError, setScheduleError] = useState('')
  const isDept = unit === 'dept'

  useEffect(() => {
    if (open) {
      const init = getDefaultScheduleTime()
      setSchedDate(init.clone())
      setSchedTime(init.clone())
      setTimeTouched(false)
      setEmails([])
    }
  }, [open])

  const handleClose = () => {
    setSchedDate(null)
    setSchedTime(null)
    setEmails([])
    setTimeTouched(false)
    setScheduleError('')
    onClose()
  }

  // 合併日期 + 時間成一個 moment，檢查是否至少15分鐘後
  const schedDateTime = combineScheduleDateTime(schedDate, schedTime)
  const isTimeValid = isScheduleTimeValid(schedDateTime)
  const timeError = timeTouched && schedDateTime && !isTimeValid ? SCHEDULE_TIME_ERROR : ''

  // 未修改時間時一律可送出（送出當下重新計算 15 分鐘後，見 resolveSubmitScheduleTime）
  const canConfirm = (!timeTouched || isTimeValid) && (!isDept || emails.length > 0)

  const handleConfirm = () => {
    setScheduleError('')
    // 對話框停留期間時間會流逝，送出當下再決定一次寄送時間，不送出注定被後端拒絕的請求
    const scheduledTime = resolveSubmitScheduleTime(schedDateTime, timeTouched)
    if (!scheduledTime) {
      setScheduleError(SCHEDULE_TIME_ERROR)
      return
    }
    const actionType = isDept ? 'transcript/sendDeptReports' : 'transcript/sendUserReports'
    const payload = isDept
      ? {
          campaignIds: [row.campaignId],
          formats: ['PDF'],
          recipientEmails: emails,
          scheduledTime: scheduledTime.toISOString(),
        }
      : {
          campaignId: row.campaignId,
          userIds: [row.userHisId],
          formats: ['PDF'],
          scheduledTime: scheduledTime.toISOString(),
        }
    dispatch({
      type: actionType,
      payload,
      successCallback: () => {
        dispatch({
          type: 'app/notify',
          payload: { message: '已排入寄送佇列', severity: 'success' },
        })
        handleClose()
      },
      errorCallback: message => {
        const errorMessage = message || '排程失敗，請稍後再試'
        setScheduleError(errorMessage)
        dispatch({
          type: 'app/notify',
          payload: { message: errorMessage, severity: 'error' },
        })
      },
    })
  }

  if (!row) return null
  const displayName = isDept ? row.groupName || row.dept || '—' : row.cname || row.name || '—'
  const subLabel = isDept ? row.campName || '—' : row.email || '—'

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: 16, pb: 1 }}>寄送排程編輯</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={0.5} mb={3} sx={{ bgcolor: '#f5f5f5', borderRadius: 2, px: 2, py: 1.5 }}>
          {isDept ? (
            <>
              <Typography fontSize={13} color="text.secondary">
                活動：{subLabel}
              </Typography>
              <Typography fontSize={13} color="text.secondary">
                群組：{displayName}
              </Typography>
            </>
          ) : (
            <>
              <Typography fontSize={13} color="text.secondary">
                姓名：{displayName}
              </Typography>
              <Typography fontSize={13} color="text.secondary">
                信箱：{subLabel}
              </Typography>
            </>
          )}
        </Stack>

        <Typography fontSize={14} fontWeight={600} color="#2D2D6C" mb={2}>
          選取寄送時間
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              value={schedDate}
              onChange={val => {
                setSchedDate(val)
                setTimeTouched(true)
              }}
              inputFormat="YYYY-MM-DD"
              disablePast
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  sx={{ width: 180, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <TimePicker
              value={schedTime}
              onChange={val => {
                setSchedTime(val)
                setTimeTouched(true)
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  error={!!timeError}
                  helperText={timeError}
                  sx={{ width: 160, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
            />
          </LocalizationProvider>
        </Stack>

        {isDept && (
          <Box mt={3}>
            <EmailChipInput emails={emails} onChange={setEmails} />
          </Box>
        )}

        {scheduleError && (
          <Typography variant="body2" color="error" mt={2}>
            {scheduleError}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5 }}>
        <Button variant="text" onClick={handleClose} sx={{ color: 'text.secondary' }}>
          取消
        </Button>
        <Button
          variant="contained"
          disabled={!canConfirm || isSending}
          onClick={handleConfirm}
          sx={{
            bgcolor: '#f97316',
            borderRadius: 2,
            px: 3,
            '&:hover': { bgcolor: '#ea6a0a' },
            '&.Mui-disabled': { bgcolor: '#fcd9b6', color: 'white' },
          }}
        >
          {isSending ? '處理中...' : '確認排程'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── 單筆信件寄送確認 Dialog ───────────────────────────────────────────────────

const SendConfirmDialog = ({ open, onClose, dispatch, row, unit = 'user', isSending }) => {
  const [emails, setEmails] = useState([])
  const [sendError, setSendError] = useState('')
  const isDept = unit === 'dept'

  const handleClose = () => {
    setEmails([])
    setSendError('')
    onClose()
  }

  const handleSend = () => {
    setSendError('')
    const actionType = isDept ? 'transcript/sendDeptReports' : 'transcript/sendUserReports'
    const payload = isDept
      ? { campaignIds: [row.campaignId], formats: ['PDF'], recipientEmails: emails }
      : { campaignId: row.campaignId, userIds: [row.userHisId], formats: ['PDF'] }
    dispatch({
      type: actionType,
      payload,
      successCallback: () => {
        dispatch({
          type: 'app/notify',
          payload: { message: '已排入寄送佇列', severity: 'success' },
        })
        handleClose()
      },
      errorCallback: message => {
        const errorMessage = message || '寄送失敗，請稍後再試'
        setSendError(errorMessage)
        dispatch({
          type: 'app/notify',
          payload: { message: errorMessage, severity: 'error' },
        })
      },
    })
  }

  if (!row) return null
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0.5 }}
      >
        <Typography component="span" fontWeight={600} fontSize={16}>
          信件寄送確認
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <Typography fontSize={18}>✕</Typography>
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography fontSize={14} fontWeight={600} color="#2D2D6C" mb={1.5}>
          {isDept ? '確認立即寄送信件給以下群組？' : '確認立即寄送信件給以下受測者？'}
        </Typography>
        <Box
          sx={{ border: '1px solid #e0e0e0', borderRadius: 2, px: 2, py: 1.5, mb: isDept ? 2 : 0 }}
        >
          {isDept ? (
            <>
              <Typography fontSize={14} mb={0.5} color="#2D2D6C">
                <strong>活動：</strong>
                {row.campName || '—'}
              </Typography>
              <Typography fontSize={14} color="#2D2D6C">
                <strong>群組：</strong>
                {row.groupName || '—'}
              </Typography>
            </>
          ) : (
            <>
              <Typography fontSize={14} mb={0.5} color="#2D2D6C">
                <strong>姓名：</strong>
                {row.cname || row.fullName || '—'}
              </Typography>
              <Typography fontSize={14} color="#2D2D6C">
                <strong>信箱：</strong>
                {row.email || '—'}
              </Typography>
            </>
          )}
        </Box>
        {isDept && (
          <Box mt={2}>
            <EmailChipInput emails={emails} onChange={setEmails} />
          </Box>
        )}
        {sendError && (
          <Typography variant="body2" color="error" mt={2}>
            {sendError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button onClick={handleClose} variant="outlined">
          取消
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          color="primary"
          disabled={(isDept && emails.length === 0) || isSending}
        >
          {isSending ? '寄送中...' : '立即寄送'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── 受測者列表子頁 ────────────────────────────────────────────────────────────

const UserListView = ({ dispatch, userList, isUserLoading, isExportingUser, isSendingUser }) => {
  const { t } = useTranslation()
  const { campId, campaignId } = useParams()

  const { data = [], paging = {} } = userList || {}

  const [keyword, setKeyword] = useState('')
  const [emailSendStatus, setEmailSendStatus] = useState('ALL')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [selected, setSelected] = useState([])
  const [downloadRow, setDownloadRow] = useState(null)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const [previewRow, setPreviewRow] = useState(null)
  const [sendRow, setSendRow] = useState(null)
  const [scheduleRow, setScheduleRow] = useState(null)

  const pagination = {
    ...paging,
    page,
    pageSize,
    onPageChange: setPage,
    onPageSizeChange: setPageSize,
  }

  const fetchData = useCallback(() => {
    // 成績單報告寄送狀態僅為前端當頁過濾（後端 emailSendStatus 參數篩選的是演練測試信狀態，
    // 語意不同，不應傳入 reportSendStatus 的篩選值），故固定不帶 emailSendStatus 給後端
    dispatch({
      type: 'transcript/getUsers',
      payload: {
        campId,
        campaignId,
        keyword,
        emailSendStatus: '',
        pageIndex: page,
        pageSize,
      },
    })
  }, [dispatch, campId, campaignId, keyword, page, pageSize])

  useEffect(() => {
    fetchData()
  }, [fetchData])
  useEffect(() => {
    setPage(0)
  }, [keyword, emailSendStatus])

  const rowKey = row => row.userHisId

  // 前端過濾：成績單報告寄送狀態（僅當頁過濾，後端尚未支援依 reportSendStatus 分頁查詢）
  const filteredData =
    emailSendStatus === 'ALL' ? data : data.filter(row => row.reportSendStatus === emailSendStatus)

  const handleSelect = key => {
    setSelected(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]))
  }

  const handleSelectAll = () => {
    const allKeys = filteredData.map(rowKey)
    setSelected(prev => (prev.length === allKeys.length ? [] : allKeys))
  }

  const cols = [
    {
      field: '__check',
      headerName: '',
      width: 70,
      sortable: false,
      renderHeader: () => (
        <Checkbox
          size="small"
          checked={filteredData.length > 0 && selected.length === filteredData.length}
          indeterminate={selected.length > 0 && selected.length < filteredData.length}
          onChange={handleSelectAll}
        />
      ),
      renderCell: ({ row }) => (
        <Checkbox
          size="small"
          checked={selected.includes(rowKey(row))}
          onChange={() => handleSelect(rowKey(row))}
          onClick={e => e.stopPropagation()}
        />
      ),
    },
    { field: 'dept', headerName: '部門名稱', width: 180 },
    { field: 'companyName', headerName: '公司名稱', width: 130 },
    { field: 'email', headerName: '信箱', width: 280 },
    { field: 'cname', headerName: '姓名', width: 100 },
    { field: 'firstName', headerName: '姓', width: 72 },
    { field: 'lastName', headerName: '名', width: 72 },
    { field: 'position', headerName: '職位', width: 110 },
    { field: 'deptGroup', headerName: '事業群', width: 100 },
    { field: 'phone', headerName: '電話', width: 140 },
    {
      field: '__preview',
      headerName: '信件預覽',
      width: 96,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Tooltip title="信件預覽">
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation()
              setPreviewRow(row)
            }}
          >
            <PreviewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: '__send',
      headerName: '信件寄送',
      width: 96,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Tooltip title="信件寄送">
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation()
              setSendRow(row)
            }}
          >
            <EmailIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: '__schedule',
      headerName: '寄送排程編輯',
      width: 120,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Tooltip title="寄送排程編輯">
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation()
              setScheduleRow(row)
            }}
          >
            <ScheduleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: 'reportSendStatus',
      headerName: '信件寄送狀態',
      width: 130,
      renderCell: ({ row }) => {
        if (row.isHeader) return null
        return <ReportSendStatusChip row={row} />
      },
    },
    {
      field: '__download',
      headerName: '下載個人報告',
      width: 130,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Tooltip title="下載個人報告">
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation()
              setDownloadRow(row)
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  return (
    <Container maxWidth={false}>
      <Grid container spacing={2} flexDirection="column">
        <Grid item>
          <Card sx={{ mt: 2 }}>
            <CardContent>
              {/* 第一列：快速搜尋 */}
              <FormCreator
                sx={{ mb: 2, '& .MuiGrid-container': { flexWrap: 'nowrap', alignItems: 'center' } }}
                formItems={[
                  {
                    label: t('general.quick_search'),
                    name: 'search',
                    defaultValue: '',
                    inputConfig: {
                      onSubmit: value => setKeyword(value),
                      onKeyUp: e => setKeyword(e.target.value),
                      sx: { width: 220 },
                    },
                  },
                ]}
                flexDirection="row"
                submitComponent={() => null}
                resetComponent={() => null}
              />

              {/* 第二列：全選 → 信件寄送狀態 → 批次按鈕 */}
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={1}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Checkbox
                    size="small"
                    checked={filteredData.length > 0 && selected.length === filteredData.length}
                    indeterminate={selected.length > 0 && selected.length < filteredData.length}
                    onChange={handleSelectAll}
                  />
                  <Typography
                    fontWeight={600}
                    sx={{ fontSize: 14, whiteSpace: 'nowrap', color: '#2D2D6C' }}
                  >
                    全選（已選 {selected.length} 位受測者）
                  </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    fontWeight={600}
                    sx={{ fontSize: 14, whiteSpace: 'nowrap', color: '#2D2D6C' }}
                  >
                    信件寄送狀態
                  </Typography>
                  <Select
                    size="small"
                    value={emailSendStatus}
                    onChange={e => setEmailSendStatus(e.target.value)}
                    sx={{
                      width: 180,
                      fontSize: 14,
                      borderRadius: '3px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2D2D6C' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2D2D6C' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#FF6700',
                        borderWidth: 2,
                      },
                    }}
                  >
                    {EMAIL_STATUS_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={selected.length === 0}
                  onClick={() => setExportDialogOpen(true)}
                >
                  批次匯出個人報告（{selected.length}）
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  disabled={selected.length === 0}
                  onClick={() => setSendDialogOpen(true)}
                >
                  批次寄送個人報告（{selected.length}）
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchData()}
                  sx={{
                    color: '#2e7d32',
                    borderColor: '#2e7d32',
                    '&:hover': { borderColor: '#1b5e20', color: '#1b5e20', bgcolor: '#f1f8e9' },
                  }}
                >
                  重新整理
                </Button>
              </Stack>

              <BatchExportDialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                dispatch={dispatch}
                actionType="transcript/exportUserReports"
                idField="userIds"
                extraPayload={{ campaignId: Number(campaignId) }}
                selectedIds={selected}
                selectedCount={selected.length}
                isExporting={isExportingUser}
                title="批次匯出個人報告"
                unit="受測者"
              />
              <BatchSendDialog
                open={sendDialogOpen}
                onClose={() => setSendDialogOpen(false)}
                dispatch={dispatch}
                actionType="transcript/sendUserReports"
                idField="userIds"
                extraPayload={{ campaignId: Number(campaignId) }}
                selectedIds={selected}
                selectedCount={selected.length}
                isSending={isSendingUser}
                title="批次寄送個人報告"
                unit="受測者"
                needRecipientEmails={false}
              />
              <BatchExportDialog
                mode="download"
                open={!!downloadRow}
                onClose={() => setDownloadRow(null)}
                dispatch={dispatch}
                actionType="transcript/exportUserReports"
                idField="userIds"
                extraPayload={{ campaignId: Number(campaignId) }}
                selectedIds={downloadRow ? [downloadRow.userHisId] : []}
                selectedCount={1}
                isExporting={isExportingUser}
                title="下載個人報告"
                unit="受測者"
              />
            </CardContent>

            <Table
              minHeight={400}
              columns={cols}
              rows={filteredData}
              dataGridConfig={{
                getRowId: row => rowKey(row),
                loading: isUserLoading,
                disableRowSelectionOnClick: true,
              }}
              noRowsLabel={t('errors.no_registered_users')}
              pagination={pagination}
            />
          </Card>
        </Grid>
      </Grid>
      <MailPreviewDialog
        open={!!previewRow}
        onClose={() => setPreviewRow(null)}
        dispatch={dispatch}
        actionType="transcript/getUserEmailPreview"
        payload={
          previewRow ? { campaignId: Number(campaignId), userHisId: previewRow.userHisId } : null
        }
      />
      <SendConfirmDialog
        open={!!sendRow}
        onClose={() => setSendRow(null)}
        dispatch={dispatch}
        row={sendRow ? { ...sendRow, campaignId: Number(campaignId) } : null}
        isSending={isSendingUser}
      />
      <ScheduleEditDialog
        open={!!scheduleRow}
        onClose={() => setScheduleRow(null)}
        dispatch={dispatch}
        row={scheduleRow ? { ...scheduleRow, campaignId: Number(campaignId) } : null}
        unit="user"
        isSending={isSendingUser}
      />
    </Container>
  )
}

// ── 部門列表主頁面 ────────────────────────────────────────────────────────────

const TranscriptPage = ({ dispatch, deptList, isDeptLoading, isExportingDept, isSendingDept }) => {
  useEffect(
    () => () => {
      if (!navigatingToUserPage) deptListStateCache = null
      navigatingToUserPage = false
    },
    [],
  )
  return (
    <DeptListView
      dispatch={dispatch}
      deptList={deptList}
      isDeptLoading={isDeptLoading}
      isExportingDept={isExportingDept}
      isSendingDept={isSendingDept}
      stateCache={deptListStateCache}
      onSaveState={s => {
        deptListStateCache = s
      }}
    />
  )
}

// ── 個人報告子頁面 ────────────────────────────────────────────────────────────

const TranscriptUserPage = ({
  dispatch,
  userList,
  isUserLoading,
  isExportingUser,
  isSendingUser,
}) => (
  <UserListView
    dispatch={dispatch}
    userList={userList}
    isUserLoading={isUserLoading}
    isExportingUser={isExportingUser}
    isSendingUser={isSendingUser}
  />
)

export default connect(mapStateToProps)(TranscriptPage)
export const ConnectedTranscriptUserPage = connect(mapStateToProps)(TranscriptUserPage)
